import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sun, Moon, Menu, X, ShoppingBag } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { MobileMenu } from "./nav/MobileMenu";
import { DesktopNav, UserMenu } from "./nav/DesktopNav";

const NAV_LINKS = [
  { label: "About", href: "#about" },
  { label: "Solutions", href: "#solutions" },
  { label: "Marketplace", href: "/marketplace", isRoute: true },
  { label: "Rapid", href: "#rapid" },
  { label: "Logistics", href: "#logistics" },
  { label: "Finance", href: "#finance" },
  { label: "Team", href: "#team" },
];

export function Navbar({ isDark, onToggleTheme }) {
  const [scrolled, setScrolled] = useState(false);
  const [shine, setShine] = useState(false);

  useEffect(() => {
    let t;
    const onStrike = () => {
      setShine(false);
      requestAnimationFrame(() => setShine(true));
      clearTimeout(t);
      t = setTimeout(() => setShine(false), 1600);
    };
    window.addEventListener("ib-strike", onStrike);
    return () => { window.removeEventListener("ib-strike", onStrike); clearTimeout(t); };
  }, []);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const handleNav = (link) => {
    setMobileOpen(false);
    if (link.isRoute) return navigate(link.href);
    const scrollTo = () => document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" });
    if (location.pathname === "/") return scrollTo();
    navigate("/");
    setTimeout(scrollTo, 300);
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-strong py-3" : "py-5 nav-top"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className={`relative flex items-baseline gap-0 ${shine ? "logo-shine" : ""}`}>
          <span className="font-unbounded font-black text-xl tracking-tighter text-ib-text">Iron</span>
          <span className="font-jetbrains font-bold text-xl text-ib-blue">Byte</span>
        </Link>

        <DesktopNav links={NAV_LINKS} pathname={location.pathname} onNav={handleNav} />

        {/* Right side */}
        <div className="flex items-center gap-3">
          <button
            data-testid="theme-toggle"
            onClick={onToggleTheme}
            className="p-2 rounded-lg hover:bg-ib-steel/20 transition-colors"
            aria-label="Toggle theme"
          >
            {isDark ? <Sun className="w-4 h-4 text-ib-text-secondary" /> : <Moon className="w-4 h-4 text-ib-text-secondary" />}
          </button>

          <UserMenu user={user} onLogout={logout} />

          <Link to="/marketplace" data-testid="nav-marketplace-btn">
            <Button
              className="hidden md:inline-flex bg-ib-blue hover:bg-ib-blue/90 text-white font-outfit text-sm gap-1.5 transition-all"
            >
              <ShoppingBag className="w-3.5 h-3.5" />
              Marketplace
            </Button>
          </Link>

          {/* Mobile hamburger */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-ib-text-secondary"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <MobileMenu
          links={NAV_LINKS}
          user={user}
          onNav={handleNav}
          onLogout={() => { logout(); setMobileOpen(false); }}
          onClose={() => setMobileOpen(false)}
        />
      )}
    </nav>
  );
}
